import { useEffect, useState } from 'react';
import { Link, NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Bell, ChevronDown, LogOut, Menu, Settings, UserCircle, X } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { initials } from '@unifemga/shared';
import { cn } from '@/lib/utils';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { ThemeToggle } from '@/components/ThemeToggle';

export interface NavItem {
  to: string;
  label: string;
  icon: LucideIcon;
  end?: boolean;
  /** Hides the item when the signed-in user lacks this permission. */
  permission?: string;
  /** Groups items under a heading in the sidebar. */
  group?: string;
}

/**
 * Shared shell for the member and admin portals.
 *
 * One component rather than two near-identical ones: the portals differ only in
 * their navigation, and duplicating the shell is how two layouts quietly drift
 * apart.
 */
export function AppShell({
  nav,
  title,
  homePath,
}: {
  nav: NavItem[];
  title: string;
  homePath: string;
}) {
  const { user, signOut, can } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  // Close the drawer on navigation, or a member taps a link and the menu stays
  // covering the page they just opened.
  useEffect(() => {
    setOpen(false);
    setMenuOpen(false);
  }, [location.pathname]);

  const visible = nav.filter((item) => !item.permission || can(item.permission as never));

  const groups = visible.reduce<Record<string, NavItem[]>>((acc, item) => {
    const key = item.group ?? '';
    (acc[key] ??= []).push(item);
    return acc;
  }, {});

  const isMember = Boolean(user?.memberId) && !user?.isAdmin;

  return (
    <div className="flex min-h-screen bg-background-alt">
      {/* Sidebar */}
      <aside
        className={cn(
          // Off-canvas drawer on small screens; a sticky full-height rail from
          // lg up, so the navigation stays put while the page scrolls.
          'fixed inset-y-0 left-0 z-50 flex w-[264px] shrink-0 flex-col border-r border-sidebar-border bg-sidebar transition-transform duration-200',
          'lg:sticky lg:top-0 lg:h-screen lg:translate-x-0',
          open ? 'translate-x-0' : '-translate-x-full',
        )}
      >
        <div className="flex h-16 shrink-0 items-center justify-between px-5">
          <Link to={homePath} className="wordmark text-xl text-sidebar-foreground">
            AAF-UNIFEMGA
          </Link>
          <button
            type="button"
            className="rounded-lg p-1 text-sidebar-foreground lg:hidden"
            onClick={() => setOpen(false)}
            aria-label="Close menu"
          >
            <X size={20} />
          </button>
        </div>

        <p className="px-5 pb-3 text-[10px] font-bold uppercase tracking-[0.18em] text-sidebar-foreground/45">
          {title}
        </p>

        <nav className="flex-1 space-y-6 overflow-y-auto px-3 pb-6">
          {Object.entries(groups).map(([group, items]) => (
            <div key={group || 'main'}>
              {group && (
                <p className="mb-1.5 px-3 text-[10px] font-bold uppercase tracking-[0.16em] text-sidebar-foreground/40">
                  {group}
                </p>
              )}
              <div className="space-y-0.5">
                {items.map(({ to, label, icon: Icon, end }) => (
                  <NavLink
                    key={to}
                    to={to}
                    end={end}
                    className={({ isActive }) =>
                      cn(
                        'group relative flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-semibold transition-all duration-150',
                        isActive
                          ? 'bg-sidebar-primary text-sidebar-primary-foreground shadow-brand'
                          : 'text-sidebar-foreground/80 hover:bg-sidebar-accent hover:text-sidebar-accent-foreground',
                      )
                    }
                  >
                    {({ isActive }) => (
                      <>
                        {/* A left rail on the active item, so the current page
                            is legible at a glance even in peripheral vision. */}
                        <span
                          className={cn(
                            'absolute -left-3 h-5 w-1 rounded-r-full bg-primary transition-opacity',
                            isActive ? 'opacity-0' : 'opacity-0',
                          )}
                        />
                        <Icon size={18} className="shrink-0" />
                        {label}
                      </>
                    )}
                  </NavLink>
                ))}
              </div>
            </div>
          ))}
        </nav>

      </aside>

      {open && (
        <div
          className="fixed inset-0 z-40 bg-foreground/40 backdrop-blur-sm lg:hidden"
          onClick={() => setOpen(false)}
          aria-hidden="true"
        />
      )}

      {/* Main */}
      <div className="flex min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-30 border-b border-border/60 bg-background/80 backdrop-blur-xl">
          <div className="flex h-16 items-center justify-between gap-4 px-5 lg:px-8">
            <button
              type="button"
              className="rounded-lg p-1.5 text-foreground transition-colors hover:bg-accent lg:hidden"
              onClick={() => setOpen(true)}
              aria-label="Open menu"
            >
              <Menu size={22} />
            </button>

            <div className="ml-auto flex items-center gap-2">
              {isMember && <NotificationBell />}
              <ThemeToggle />

              {/* Account menu */}
              <div className="relative">
                <button
                  type="button"
                  onClick={() => setMenuOpen((value) => !value)}
                  className="flex items-center gap-2 rounded-full border border-border bg-background py-1 pl-1 pr-2 transition-colors hover:bg-accent"
                  aria-expanded={menuOpen}
                  aria-haspopup="menu"
                >
                  <span className="flex h-7 w-7 items-center justify-center rounded-full bg-primary text-[11px] font-bold text-primary-foreground">
                    {user ? initials({ firstName: user.email, lastName: user.email.slice(1) }) : '—'}
                  </span>
                  <span className="hidden max-w-[160px] truncate text-sm font-semibold text-foreground sm:block">
                    {user?.email}
                  </span>
                  <ChevronDown size={14} className="text-foreground-secondary" />
                </button>

                {menuOpen && (
                  <>
                    <div
                      className="fixed inset-0 z-40"
                      onClick={() => setMenuOpen(false)}
                      aria-hidden="true"
                    />
                    <div
                      role="menu"
                      className="absolute right-0 z-50 mt-2 w-60 overflow-hidden rounded-xl border border-border bg-popover shadow-floating"
                    >
                      <div className="border-b border-border px-4 py-3">
                        <p className="truncate text-sm font-semibold text-popover-foreground">
                          {user?.email}
                        </p>
                        <p className="mt-0.5 text-xs text-foreground-secondary">
                          {user?.roleName?.replace(/_/g, ' ').toLowerCase()}
                        </p>
                      </div>

                      {isMember && (
                        <>
                          <MenuLink to="/portal/profile" icon={UserCircle} label="Profile" />
                          <MenuLink to="/portal/settings" icon={Settings} label="Settings" />
                        </>
                      )}

                      <button
                        type="button"
                        role="menuitem"
                        onClick={() => {
                          void signOut().then(() => navigate('/login', { replace: true }));
                        }}
                        className="flex w-full items-center gap-2.5 border-t border-border px-4 py-2.5 text-left text-sm font-semibold text-danger transition-colors hover:bg-danger-soft"
                      >
                        <LogOut size={15} /> Sign out
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </header>

        <main className="flex-1 px-5 py-8 lg:px-8 lg:py-10">
          <Outlet />
        </main>
      </div>
    </div>
  );
}

function MenuLink({ to, icon: Icon, label }: { to: string; icon: LucideIcon; label: string }) {
  return (
    <Link
      to={to}
      role="menuitem"
      className="flex items-center gap-2.5 px-4 py-2.5 text-sm font-semibold text-popover-foreground transition-colors hover:bg-accent"
    >
      <Icon size={15} /> {label}
    </Link>
  );
}

/** Unread count from the in-app notification centre (§49). */
function NotificationBell() {
  const { data } = useQuery({
    queryKey: ['inbox'],
    queryFn: () => api.get<{ unreadCount: number }>('/notifications/inbox'),
    refetchInterval: 60_000,
  });

  const unread = data?.unreadCount ?? 0;

  return (
    <Link
      to="/portal/notifications"
      className="relative inline-flex h-9 w-9 items-center justify-center rounded-lg border border-border bg-background text-foreground-secondary transition-colors hover:bg-accent hover:text-accent-foreground"
      aria-label={unread > 0 ? `${unread} unread notifications` : 'Notifications'}
    >
      <Bell size={16} />
      {unread > 0 && (
        <span className="absolute -right-1 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-danger px-1 text-[10px] font-bold text-danger-foreground">
          {unread > 9 ? '9+' : unread}
        </span>
      )}
    </Link>
  );
}
